import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import type { AnimationItem } from 'lottie-web'

import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { useNextStep } from 'hooks/useNextStep'

import { TPageProps } from 'models/common.model'

import { Color, Language } from 'root-constants'

import {
  LoadingProgress,
  useLoadingStages,
} from '../components/LoadingProgress'
import { StyledKnowledgeQuizIntro as S } from './KnowledgeQuizIntro.styles'
import {
  INITIAL_PEOPLE_COUNT,
  LOADER_DURATION_MS,
  PEOPLE_COUNT_INTERVAL_MS,
  QUIZ_LOADING_LABEL,
  QUIZ_LOADING_STAGES,
} from './constants'

export const KnowledgeQuizIntro: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
}) => {
  const { t, i18n } = useTranslation()
  const { handleChange } = useDelayedContinue()

  const [peopleCount, setPeopleCount] = useState(INITIAL_PEOPLE_COUNT)

  const animationContainerRef = useRef<HTMLDivElement>(null)

  const handleContinue = useNextStep({
    pageId,
    question: t('onboarding.knowledgeQuizIntro.title', { lng: Language.EN }),
    nextPagePath,
  })

  const { counter, labelKey, isLabelHidden } = useLoadingStages({
    duration: LOADER_DURATION_MS,
    initialLabelKey: QUIZ_LOADING_LABEL,
    stages: QUIZ_LOADING_STAGES,
    onFinish: () => handleChange('', handleContinue),
  })

  useEffect(() => {
    const intervalId = setInterval(() => {
      setPeopleCount((count) => {
        const direction = Math.random() < 0.5 ? -1 : 1
        const step = Math.floor(Math.random() * 2) + 1

        return count + direction * step
      })
    }, PEOPLE_COUNT_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    let animation: AnimationItem | undefined
    let isUnmounted = false

    Promise.all([
      import('lottie-web/build/player/lottie_light'),
      import('assets/animation/AP_quiz_welcome.json'),
    ]).then(([{ default: lottie }, { default: animationData }]) => {
      const container = animationContainerRef.current

      if (isUnmounted || !container) return

      animation = lottie.loadAnimation({
        container,
        animationData,
        loop: true,
        autoplay: true,
      })
    })

    return () => {
      isUnmounted = true
      animation?.destroy()
    }
  }, [])

  return (
    <S.Container>
      <S.Animation ref={animationContainerRef} />

      <S.Title>{t('onboarding.knowledgeQuizIntro.title')}</S.Title>
      <S.Subtitle>{t('onboarding.knowledgeQuizIntro.subtitle')}</S.Subtitle>

      <S.Loader>
        <LoadingProgress
          loadingLabel={t(labelKey)}
          isLabelHidden={isLabelHidden}
          progressLabel={
            <Trans
              i18nKey="onboarding.introAyah.counter"
              values={{ counter }}
            />
          }
          value={counter}
          labelColor={Color.INK_BASE}
          hasWhiteTrack
        />

        <S.SocialProof>
          {t('onboarding.knowledgeQuizIntro.takingQuizNow', {
            peopleCount: peopleCount.toLocaleString(i18n.language),
          })}
        </S.SocialProof>
      </S.Loader>
    </S.Container>
  )
}
